import { EvaluationAssessmentType, EvaluationType } from './Evaluation'

export interface INotificationPure {
  _id?: any
  dateOpened?: Date
  dateCreated: Date
  // Set once the mail that follows this notification went out (results ready)
  emailSent?: Date
  memberId: string
  initiator?: {
    memberId: string
    name: string
  }
  type: NotificationType
  context?: {
    feedbackId?: string
    companyName?: string
    plannerId?: string
    plannerName?: string
    onboardingId?: string
    completedTasks?: number
    totalTasks?: number
    weeksLeft?: number
    termNumber?: number
    pdpId?: string
    /**
     * Identifies the journal a journal notification is about, so the row can be
     * cleaned up when that journal is deleted. Optional: rows created before it
     * was added carry only a journalTitle snapshot.
     */
    journalId?: string
    journalTitle?: string
    journalOwnerId?: string
    evaluationType?: EvaluationType
    // A name that points at something renameable carries its id, so a rename can
    // update it (NotificationsService.renameInContext). Optional: older rows lack it.
    teamId?: string
    teamName?: string
    distributionId?: string
    surveyName?: string
    jobLevelId?: string
    jobTitle?: string
    moment?: ResultsMoment
    // The sections of the results that became visible with this notification
    parts?: EvaluationAssessmentType[]
  }
}

// What made a member's evaluation results visible: the round closed, an admin
// revealed them (manual reveal), or the assessors finished (the other reveal options)
export const RESULTS_MOMENTS = ['closed', 'revealed', 'complete'] as const
export type ResultsMoment = typeof RESULTS_MOMENTS[number]

// Types of notifications which can be used for creation
export type AllowedNotification =
  | FeedbackReceivedNotification
  | FeedbackReceivedExternalNotification
  | FeedbackRevokedNotification
  | GoalCommentNotification
  | GoalsUnpublishedTeamLeadNotification
  | GoalsUnpublishedCustomLeadNotification
  | GoalsReminderNotification
  | JournalInactivityNotification
  | JournalNewEntryNotification
  | JournalAccessLostNotification
  | CollectionNewNotification
  | CollectionNoteNotification
  | EvaluationRevokedNotification
  | EvaluationReceivedNotification
  | BecameCustomLeadNotification
  | StoppedBeingCustomLeadNotification
  | BecameLeadNotification
  | BecameBuddyNotification
  | NewMemberInTeamNotification
  | OnboardingRunningLateNotification
  | OnboardingCompleteNotification
  | OnboardingProgressNotification
  | OnboardingStartedNotification
  | EvaluationResultsAvailableNotification
  | GoalsReviewedNotification
  | GoalsCarriedOverNotification
  | JournalAccessGrantedNotification
  | BecameAdminNotification
  | StoppedBeingAdminNotification
  | GainedViewAllNotification
  | LostViewAllNotification
  | TeamGoalsPublishedNotification
  | TeamSelfEvaluationSubmittedNotification
  | TeamFeedbackExchangedNotification
  | CompanyMemberJoinedNotification
  | CompanyMemberArchivedNotification
  | CompanyTermStartedNotification
  | CompanyEvaluationsStartedNotification
  | CompanyEvaluationsClosedNotification
  | CompanySurveyCompletedNotification
  | TeammateJoinedNotification
  | TeammateLeftNotification
  | JobUpdatedNotification

// This type is used to maintain the correct notification context after creation
export type CreatedNotification<T extends AllowedNotification> = T &
  INotificationPure

export enum NotificationType {
  FEEDBACK_RECEIVED = 'feedback_received',
  FEEDBACK_RECEIVED_EXTERNAL = 'feedback_received_external',
  FEEDBACK_REVOKED = 'feedback_revoked',
  GOAL_COMMENT = 'goal_comment',
  GOALS_UNPUBLISHED_TEAM_LEAD = 'goals_unpublished_team_lead',
  GOALS_UNPUBLISHED_CUSTOM_LEAD = 'goals_unpublished_custom_lead',
  JOURNAL_INACTIVITY = 'journal_inactivity',
  JOURNAL_ACCESS_LOST = 'journal_access_lost',
  JOURNAL_NEW_ENTRY = 'journal_new_entry',
  GOALS_REMINDER = 'goals_reminder',
  COLLECTION_NEW = 'collection_new',
  COLLECTION_NOTE = 'collection_note',
  EVALUATION_REVOKED = 'evaluation_revoked',
  EVALUATION_RECEIVED = 'evaluation_received',
  BECAME_CUSTOM_LEAD = 'became_custom_lead',
  STOPPED_BEING_CUSTOM_LEAD = 'stopped_being_custom_lead',
  BECAME_LEAD = 'became_lead',
  BECAME_BUDDY = 'became_buddy',
  NEW_MEMBER_IN_TEAM = 'new_member_in_team',
  ONBOARDING_RUNNING_LATE = 'onboarding_running_late',
  ONBOARDING_COMPLETE = 'onboarding_complete',
  ONBOARDING_PROGRESS = 'onboarding_progress',
  ONBOARDING_STARTED = 'onboarding_started',
  // About the member themselves
  EVALUATION_RESULTS_AVAILABLE = 'evaluation_results_available',
  GOALS_REVIEWED = 'goals_reviewed',
  GOALS_CARRIED_OVER = 'goals_carried_over',
  JOURNAL_ACCESS_GRANTED = 'journal_access_granted',
  BECAME_ADMIN = 'became_admin',
  STOPPED_BEING_ADMIN = 'stopped_being_admin',
  GAINED_VIEW_ALL = 'gained_view_all',
  LOST_VIEW_ALL = 'lost_view_all',
  // About the people a lead guides
  TEAM_GOALS_PUBLISHED = 'team_goals_published',
  TEAM_SELF_EVALUATION_SUBMITTED = 'team_self_evaluation_submitted',
  TEAM_FEEDBACK_EXCHANGED = 'team_feedback_exchanged',
  // About the organisation, for admins
  COMPANY_MEMBER_JOINED = 'company_member_joined',
  COMPANY_MEMBER_ARCHIVED = 'company_member_archived',
  COMPANY_TERM_STARTED = 'company_term_started',
  COMPANY_EVALUATIONS_STARTED = 'company_evaluations_started',
  COMPANY_EVALUATIONS_CLOSED = 'company_evaluations_closed',
  COMPANY_SURVEY_COMPLETED = 'company_survey_completed',
  // About the member's place in the organisation
  TEAMMATE_JOINED = 'teammate_joined',
  TEAMMATE_LEFT = 'teammate_left',
  JOB_UPDATED = 'job_updated',
}

// Notification types per feature module, used to filter the weekly summary when a
// module is disabled. Lead/team-structure notifications are module-independent (kept).
export const PERFORMANCE_MANAGEMENT_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.FEEDBACK_RECEIVED,
  NotificationType.FEEDBACK_RECEIVED_EXTERNAL,
  NotificationType.FEEDBACK_REVOKED,
  NotificationType.GOAL_COMMENT,
  NotificationType.GOALS_UNPUBLISHED_TEAM_LEAD,
  NotificationType.GOALS_UNPUBLISHED_CUSTOM_LEAD,
  NotificationType.GOALS_REMINDER,
  NotificationType.COLLECTION_NEW,
  NotificationType.COLLECTION_NOTE,
  NotificationType.EVALUATION_REVOKED,
  NotificationType.EVALUATION_RECEIVED,
  NotificationType.EVALUATION_RESULTS_AVAILABLE,
  NotificationType.GOALS_REVIEWED,
  NotificationType.GOALS_CARRIED_OVER,
  NotificationType.TEAM_GOALS_PUBLISHED,
  NotificationType.TEAM_SELF_EVALUATION_SUBMITTED,
  NotificationType.TEAM_FEEDBACK_EXCHANGED,
  NotificationType.COMPANY_EVALUATIONS_STARTED,
  NotificationType.COMPANY_EVALUATIONS_CLOSED,
]

// Journals are off whenever performance management is, so this covers that too
export const JOURNAL_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.JOURNAL_INACTIVITY,
  NotificationType.JOURNAL_ACCESS_LOST,
  NotificationType.JOURNAL_NEW_ENTRY,
  NotificationType.JOURNAL_ACCESS_GRANTED,
]

// Role and company-lifecycle notifications are module-independent, exactly like the
// lead/team-structure ones above: BECAME_ADMIN, STOPPED_BEING_ADMIN,
// GAINED_VIEW_ALL, LOST_VIEW_ALL, COMPANY_MEMBER_JOINED, COMPANY_MEMBER_ARCHIVED, COMPANY_TERM_STARTED,
// TEAMMATE_JOINED, TEAMMATE_LEFT and JOB_UPDATED deliberately appear in neither
// array.

export const ONBOARDING_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.ONBOARDING_RUNNING_LATE,
  NotificationType.ONBOARDING_COMPLETE,
  NotificationType.ONBOARDING_PROGRESS,
  NotificationType.ONBOARDING_STARTED,
]

export const ENGAGEMENT_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.COMPANY_SURVEY_COMPLETED,
]

// ------------------ Feedback notifications ------------------

export type FeedbackReceivedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.FEEDBACK_RECEIVED
  context: {
    feedbackId: string
    plannerId?: string
    plannerName?: string
  }
  initiator: {
    memberId: string
    name: string
  }
}

export type FeedbackReceivedExternalNotification = Omit<
  INotificationPure,
  'initiator'
> & {
  type: NotificationType.FEEDBACK_RECEIVED_EXTERNAL
  initiator: {
    name?: string
  }
  context: {
    companyName: string
  }
}

export type FeedbackRevokedNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.FEEDBACK_REVOKED
  initiator: {
    name: string
    memberId: string
  }
}

// ------------------ Goals notifications ------------------

export type GoalCommentNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.GOAL_COMMENT
  context: {
    pdpId: string
  }
  initiator: {
    memberId: string
    name: string
  }
}

export type GoalsReminderNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.GOALS_REMINDER
  context: {
    pdpId: string
  }
}

export type GoalsUnpublishedTeamLeadNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.GOALS_UNPUBLISHED_TEAM_LEAD
}

export type GoalsUnpublishedCustomLeadNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.GOALS_UNPUBLISHED_CUSTOM_LEAD
  initiator: {
    memberId: string
    name: string
  }
}

// ------------------ Journals notifications ------------------

/** When a journal has been inactive for two months */
export type JournalInactivityNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.JOURNAL_INACTIVITY
  context: {
    journalTitle: string
    journalId?: string
  }
}

/** @param initiator is the journal owner */
export type JournalAccessLostNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.JOURNAL_ACCESS_LOST
  context: {
    journalTitle: string
    journalId?: string
  }
  initiator: {
    name: string
    memberId: string
  }
}

export type JournalNewEntryNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.JOURNAL_NEW_ENTRY
  context: {
    journalTitle: string
    journalId?: string
    // Absent on rows created before this was added; the url falls back to the
    // recipient's own journals then.
    journalOwnerId?: string
  }
  initiator: {
    name: string
    memberId: string
  }
}

// ------------------ Term review notifications ------------------

// These three carry the term they are about, so the term reviews page can point
// at the round with news. Rows created before 2026-10-06 have no term.
export type CollectionNewNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.COLLECTION_NEW
  context: {
    termNumber: number
  }
}

export type CollectionNoteNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.COLLECTION_NOTE
  initiator: {
    memberId: string
    name: string
  }
  context: {
    termNumber: number
  }
}

/** @param initiator refers to the evaluated member, not necessarily the one revoking the request.
 * For example admin could revoke the request but initiator would still be the evaluated member */
export type EvaluationRevokedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.EVALUATION_REVOKED
  initiator?: {
    memberId: string
    name: string
  }
}

export type EvaluationReceivedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.EVALUATION_RECEIVED
  initiator: {
    memberId: string
    name: string
  }
  context: {
    evaluationType: EvaluationType
    termNumber: number
  }
}

// ------------------ User state notifications ------------------

/** @param initiator refers to led member */
export type BecameCustomLeadNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.BECAME_CUSTOM_LEAD
  initiator: {
    memberId: string
    name: string
  }
}

/** @param initiator refers to the no longer led member */
export type StoppedBeingCustomLeadNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.STOPPED_BEING_CUSTOM_LEAD
  initiator: {
    memberId: string
    name: string
  }
}

/** @param initiator refers to the buddy */
export type BecameBuddyNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.BECAME_BUDDY
  initiator: {
    memberId: string
    name: string
  }
}

export type BecameLeadNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.BECAME_LEAD
  context: {
    teamId?: string
    teamName: string
  }
}

/** @param initiator is the new member */
export type NewMemberInTeamNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.NEW_MEMBER_IN_TEAM
  initiator: {
    memberId: string
    name: string
  }
}

// ------------------ Onboarding state notifications ------------------
export type OnboardingRunningLateNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.ONBOARDING_RUNNING_LATE
  initiator: {
    memberId: string
    name: string
  }
  context: {
    onboardingId: string
  }
}

export type OnboardingCompleteNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.ONBOARDING_COMPLETE
  initiator: {
    memberId: string
    name: string
  }
  context: {
    onboardingId: string
  }
}

export type OnboardingProgressNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.ONBOARDING_PROGRESS
  initiator: {
    memberId: string
    name: string
  }
  context: {
    onboardingId: string
    completedTasks: number
    totalTasks: number
    weeksLeft: number
  }
}

export type OnboardingStartedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.ONBOARDING_STARTED
  initiator: {
    memberId: string
    name: string
  }
  context: {
    onboardingId: string
  }
}

// ------------------ Personal development notifications ------------------

/** Results became visible to the member, which is later than "an assessor submitted". */
export type EvaluationResultsAvailableNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.EVALUATION_RESULTS_AVAILABLE
  context: {
    termNumber: number
    moment: ResultsMoment
    parts: EvaluationAssessmentType[]
  }
}

/** @param initiator is the lead who reviewed the goals */
export type GoalsReviewedNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.GOALS_REVIEWED
  context: {
    pdpId: string
  }
  initiator: {
    memberId: string
    name: string
  }
}

/** Goals were carried into the new term, so new ones can be added on top. */
export type GoalsCarriedOverNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.GOALS_CARRIED_OVER
  context: {
    termNumber: number
  }
}

/** @param initiator is the journal owner. Mirror of JOURNAL_ACCESS_LOST. */
export type JournalAccessGrantedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.JOURNAL_ACCESS_GRANTED
  context: {
    journalTitle: string
    journalId?: string
  }
  initiator: {
    memberId: string
    name: string
  }
}

export type BecameAdminNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.BECAME_ADMIN
}

export type StoppedBeingAdminNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.STOPPED_BEING_ADMIN
}

export type GainedViewAllNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.GAINED_VIEW_ALL
}

export type LostViewAllNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.LOST_VIEW_ALL
}

// ------------------ Lead insight notifications ------------------

/** @param initiator is the led member the notification is about */
export type TeamGoalsPublishedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.TEAM_GOALS_PUBLISHED
  initiator: {
    memberId: string
    name: string
  }
}

/** @param initiator is the led member who submitted */
export type TeamSelfEvaluationSubmittedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.TEAM_SELF_EVALUATION_SUBMITTED
  initiator: {
    memberId: string
    name: string
  }
}

/**
 * One per exchange rather than a periodic count: bundling in the activity panel
 * turns several of these into "from Anna and 2 others" on its own.
 * @param initiator is the member who gave the feedback
 */
export type TeamFeedbackExchangedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.TEAM_FEEDBACK_EXCHANGED
  initiator: {
    memberId: string
    name: string
  }
}

// ------------------ Company notifications (admins) ------------------

/** @param initiator is the new member */
export type CompanyMemberJoinedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.COMPANY_MEMBER_JOINED
  initiator: {
    memberId: string
    name: string
  }
}

/** @param initiator is the archived member */
export type CompanyMemberArchivedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.COMPANY_MEMBER_ARCHIVED
  initiator: {
    memberId: string
    name: string
  }
}

export type CompanyTermStartedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.COMPANY_TERM_STARTED
  context: {
    termNumber: number
  }
}

export type CompanyEvaluationsStartedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.COMPANY_EVALUATIONS_STARTED
  context: {
    termNumber: number
  }
}

export type CompanyEvaluationsClosedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.COMPANY_EVALUATIONS_CLOSED
  context: {
    termNumber: number
  }
}

/** Everyone invited answered, so the survey closed before its end date. */
export type CompanySurveyCompletedNotification = Omit<
  INotificationPure,
  'context'
> & {
  type: NotificationType.COMPANY_SURVEY_COMPLETED
  context: {
    distributionId: string
    surveyName: string
  }
}

// ------------------ Team and job notifications ------------------

/** Someone joined the viewer's team. Goes to the teammates; the leads get
 * NEW_MEMBER_IN_TEAM instead so nobody receives both. */
export type TeammateJoinedNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.TEAMMATE_JOINED
  initiator: {
    memberId: string
    name: string
  }
}

/** Someone left the viewer's team, by moving to another team or to none. */
export type TeammateLeftNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.TEAMMATE_LEFT
  initiator: {
    memberId: string
    name: string
  }
}

/** An admin or lead changed the member's job level. The initiator is whoever
 * made the change; jobTitle is the new level, which is what "Baan" means to the
 * member. */
export type JobUpdatedNotification = Omit<INotificationPure, 'context'> & {
  type: NotificationType.JOB_UPDATED
  initiator: {
    memberId: string
    name: string
  }
  context: {
    jobLevelId?: string
    jobTitle: string
  }
}
